/**
 * useShareExport.ts
 *
 * Owns the Share & Export panel's state: analysing the active file, then
 * optimising/downloading a copy or exporting a PDF. Every async action traps
 * its own errors into state (never throws to the caller) and reports success
 * via a short-lived toast message, mirroring useBrandCheck/useBrandApply's
 * status-based hooks elsewhere in this feature set.
 */
import { useCallback, useState } from "react";
import { logOfficeError } from "../../../../../telemetry";
import { useTimedToast } from "../../shared/hooks/useTimedToast";
import {
  deriveOptimizedFilename, derivePdfFilename, downloadBytes,
  getActivePresentationBuffer, PPTX_MIME_TYPE,
} from "../../../utils/officePptx";
import type { PdfExportProgress, ShareFileAnalysis, ShareOptimizeOptions, ShareOptimizeResult } from "../../../types";
import { analyzePresentationBytes } from "../services/pptxAnalyzer";
import { optimizePresentationBytes } from "../services/pptxOptimizer";
import { exportPresentationAsPdf } from "../services/pdfExport";

const TOAST_DURATION_MS = 3500;
const SIZE_UNITS = ["B", "KB", "MB", "GB"] as const;

/** `bytes < 0` and non-finite input (NaN, Infinity) both render as "—"
 * rather than a misleading number — neither can come from a real file, but
 * a corrupt analysis result should degrade gracefully, not crash the panel. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes === 0) return "0 B";
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), SIZE_UNITS.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${SIZE_UNITS[exponent]}`;
}

export type SharePhase = "idle" | "analyzing" | "analyzed";
export type ShareBusyAction = "optimizing" | "exportingPdf" | null;

export interface ShareExportState {
  phase: SharePhase;
  analysis: ShareFileAnalysis | null;
  errorMessage: string | null;
  busy: ShareBusyAction;
  pdfProgress: PdfExportProgress | null;
}

const INITIAL_STATE: ShareExportState = {
  phase: "idle", analysis: null, errorMessage: null, busy: null, pdfProgress: null,
};

function toErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function buildOptimizeToastMessage(options: ShareOptimizeOptions, result: ShareOptimizeResult): string {
  const clauses: string[] = [];
  if (options.compressImages) clauses.push(`images @ ${options.imageQuality}%`);
  if (options.removeUnusedLayouts) {
    clauses.push(result.layoutsRemoved > 0 ? "unused layouts removed" : "no unused layouts found");
  }
  const detail = clauses.length > 0 ? ` — ${clauses.join(", ")}` : "";
  const sizes = `(${formatFileSize(result.originalSizeBytes)} → ${formatFileSize(result.newSizeBytes)})`;
  return `Optimised PPTX downloaded${detail} ${sizes}.`;
}

export interface UseShareExport {
  readonly state: ShareExportState;
  readonly toast: string | null;
  readonly analyze: () => Promise<void>;
  readonly optimizeAndDownload: (options: ShareOptimizeOptions) => Promise<void>;
  readonly exportPdf: () => Promise<void>;
  readonly reset: () => void;
}

export function useShareExport(): UseShareExport {
  const [state, setState] = useState<ShareExportState>(INITIAL_STATE);
  const { toast, showToast } = useTimedToast(TOAST_DURATION_MS);

  const analyze = useCallback(async () => {
    setState((s) => ({ ...s, phase: "analyzing", errorMessage: null }));
    try {
      const buffer = await getActivePresentationBuffer();
      const analysis = await analyzePresentationBytes(buffer);
      setState({ phase: "analyzed", analysis, errorMessage: null, busy: null, pdfProgress: null });
    } catch (err) {
      logOfficeError(err, "share-export-analyze");
      // A re-analyze failure keeps whatever analysis is already on screen
      // (still useful) rather than wiping it — only a first-ever attempt
      // with nothing to fall back on drops back to "idle".
      setState((s) => ({
        ...s,
        phase: s.analysis ? "analyzed" : "idle",
        errorMessage: toErrorMessage(err, "Could not analyse this presentation."),
      }));
    }
  }, []);

  const optimizeAndDownload = useCallback(async (options: ShareOptimizeOptions) => {
    setState((s) => ({ ...s, busy: "optimizing", errorMessage: null }));
    try {
      const buffer = await getActivePresentationBuffer();
      const result = await optimizePresentationBytes(buffer, options);
      downloadBytes(result.bytes, deriveOptimizedFilename(), PPTX_MIME_TYPE);
      setState((s) => ({ ...s, busy: null }));
      showToast(buildOptimizeToastMessage(options, result));
    } catch (err) {
      logOfficeError(err, "share-export-optimize");
      setState((s) => ({ ...s, busy: null, errorMessage: toErrorMessage(err, "Could not optimise this presentation.") }));
    }
  }, [showToast]);

  const exportPdf = useCallback(async () => {
    setState((s) => ({ ...s, busy: "exportingPdf", errorMessage: null, pdfProgress: { current: 0, total: 0 } }));
    try {
      await exportPresentationAsPdf(derivePdfFilename(), (progress) => {
        setState((s) => ({ ...s, pdfProgress: progress }));
      });
      setState((s) => ({ ...s, busy: null, pdfProgress: null }));
      showToast("PDF exported.");
    } catch (err) {
      // exportPresentationAsPdf already traces the underlying failure —
      // only the panel-facing state update happens here.
      setState((s) => ({
        ...s, busy: null, pdfProgress: null,
        errorMessage: toErrorMessage(err, "Could not export this presentation as a PDF."),
      }));
    }
  }, [showToast]);

  const reset = useCallback(() => setState(INITIAL_STATE), []);

  return { state, toast, analyze, optimizeAndDownload, exportPdf, reset };
}
